import { useEffect, useState } from 'react';

import { FormApi } from 'final-form';
import _debounce from 'lodash/debounce';
import _identity from 'lodash/identity';
import _isArray from 'lodash/isArray';
import _isPlainObject from 'lodash/isPlainObject';
import _mergeWith from 'lodash/mergeWith';
import _unionBy from 'lodash/unionBy';
import { useSelector } from 'react-redux';

import { useInquiryProcessConfig } from 'new/form/state/inquiryProcessConfig/hooks';
import { getNewInquiryDetails } from 'store/newInquiryDetails/selector';
import { isE2ETest } from 'utils/isE2ETest';

export const FORM_VALUES_STORAGE_KEY = 'newFormValues';
export const INQUIRY_PROGRESS_STORAGE_KEY = 'newInquiryProgress';

const SENSITIVE_FIELDS = [
  'password',
  'passwordConfirmation',
  'pinNumber',
  'iban',
  'bic',
  'partner-specific-iban',
];

const omitSensitiveFields = (obj: any): any => {
  if (_isArray(obj)) {
    return obj.map(omitSensitiveFields);
  }

  if (_isPlainObject(obj)) {
    return Object.keys(obj).reduce((acc: Record<string, any>, key) => {
      if (!SENSITIVE_FIELDS.includes(key)) {
        acc[key] = omitSensitiveFields(obj[key]);
      }
      return acc;
    }, {} as Record<string, any>);
  }

  return obj;
};

export const createPersistDecorator = (key: string, storage = window.sessionStorage, wait = 50) => {
  return (form: FormApi) => {
    const saveState = _debounce((values) => {
      // Only persist sensitive fields in e2e tests for easier debugging
      // In development and production, always omit sensitive fields for security
      const sanitizedValues = isE2ETest() ? values : omitSensitiveFields(values);
      storage.setItem(key, JSON.stringify(sanitizedValues));
    }, wait);

    const unsubscribe = form.subscribe(
      ({ values }) => {
        saveState(values);
      },
      { values: true },
    );

    return unsubscribe;
  };
};

export const persistDecorator = createPersistDecorator(FORM_VALUES_STORAGE_KEY);

export function useGetInitialValuesForCreateMode(): any | null {
  const values = sessionStorage.getItem(FORM_VALUES_STORAGE_KEY);
  if (values) {
    return JSON.parse(values);
  }
  return null;
}

type GetInitialValuesForEditModeParams = {
  inquiryDetails: any;
  initialValues: any;
};

/**
 * The inquiryDetails we receive from the backend might not contain all the values that we need to start the inquiry
 * process, so we are deepMerging with the initialValues to make sure that the structure of the form values is
 * correct, and we do not run into any errors. We are also cloning the initialValues to not change the initialValues
 * object.
 */
function getInitialValuesForEditMode({
  inquiryDetails,
  initialValues,
}: GetInitialValuesForEditModeParams) {
  let parsedValues = {};
  const values = sessionStorage.getItem(FORM_VALUES_STORAGE_KEY);
  if (values) {
    parsedValues = JSON.parse(values);
  }

  const mergedValues = _mergeWith({}, initialValues, inquiryDetails, parsedValues, customizer);

  return mergedValues;
}

export function useGetInitialValuesForEditMode() {
  const inquiryDetails = useSelector(getNewInquiryDetails);
  const {
    formStateData: { initialFormValues },
  } = useInquiryProcessConfig();

  const [initialValues, setInitialValues] = useState(initialFormValues);
  useEffect(() => {
    const initialValues = getInitialValuesForEditMode({
      inquiryDetails,
      initialValues: initialFormValues,
    });
    setInitialValues(initialValues);
  }, [inquiryDetails, initialFormValues]);

  return initialValues;
}

const filterArray = (arr: Array<any>): Array<any> => {
  // Step 1: Return the array as-is if it has only one item
  if (arr.length <= 1) {
    return arr;
  }

  // Step 2: Apply filtering criteria
  const filtered = arr.filter((item: any) => {
    if (item === null) return false;
    const keys = Object.keys(item);
    if (keys.length === 0) return false; // Filter out empty objects

    // Filter out objects with all empty or undefined keys
    return keys.some((key) => item[key] !== '' && item[key] !== undefined);
  });

  // Step 3: Ensure at least one empty object
  if (filtered.length === 0) {
    filtered.push({});
  }

  return filtered;
};

const mergeArrays = (objArr: any[], srcArr: any[], key: string): any => {
  // Most of saved data has 'id' property, you can use `key` parameter to differentiate between different arrays
  // for now, this should work for arrays like `self-beneficiary-owner`, `beneficiary-owner`, `user-representative`
  const mergedArray = _unionBy(objArr, srcArr, (obj: any) => {
    return obj?.id || _identity(obj);
  });

  return filterArray(mergedArray);
};

const customizer = (objValue: any, srcValue: any, key: string): any => {
  if (_isArray(objValue) && _isArray(srcValue)) {
    return mergeArrays(objValue, srcValue, key);
  } else if (_isPlainObject(objValue) && _isPlainObject(srcValue)) {
    return _mergeWith({}, objValue, srcValue, customizer);
  }
  return srcValue === undefined ? objValue : srcValue;
};

/**
 * Merges initial form values with props initial values to ensure the complete
 * structure is maintained. This is important because the form needs the full structure
 * from initialFormValues, but values should come from propsInitialValues when available.
 *
 * Note: Session storage is already handled by useGetInitialValuesForCreateMode and
 * useGetInitialValuesForEditMode hooks - don't read it here to avoid double-merging.
 *
 * @param initialFormValues - Default form values from config
 * @param propsInitialValues - Initial values passed as props (e.g., inquiry details in edit mode,
 *                             or session storage values in create mode)
 */
export function mergeFormValues<T>(initialFormValues: T, propsInitialValues: T | null): T {
  if (!propsInitialValues) {
    return initialFormValues;
  }
  return _mergeWith({}, initialFormValues, propsInitialValues, customizer);
}

const resetPersistFormValues = () => {
  sessionStorage.removeItem(FORM_VALUES_STORAGE_KEY);
};

const resetPersistInquiryProgress = () => {
  sessionStorage.removeItem(INQUIRY_PROGRESS_STORAGE_KEY);
};

export const resetPersist = () => {
  resetPersistFormValues();
  resetPersistInquiryProgress();
};
